import React from "react";
import Grid from "@mui/material/Grid2";
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import FieldingTable from "./utils/FieldingTable";

const teamColors: { [id: string]: string[] } = {
  TBR: ["#02285C", "#90BDE7", "#FFFFFF"],
  BAL: ["#F25D23", "#000000", "#FFFFFF"],
  NYY: ["#132448", "#FFFFFF", "#FFFFFF"],
  BOS: ["#BD3039", "#0D2B56", "#FFFFFF"],
  TOR: ["#134A8E", "#E8291C", "#FFFFFF"],
  CLE: ["#E31937", "#002B5C", "#FFFFFF"],
  KCR: ["#004687", "#FFFFFF", "#FFFFFF"],
  DET: ["#0C2C56", "#FFFFFF", "#FFFFFF"],
  CHW: ["#000000", "#BFBFBF", "#FFFFFF"],
  MIN: ["#091F40", "#E31E34", "#FFFFFF"],
  HOU: ["#002D62", "#F4911E", "#FFFFFF"],
  SEA: ["#005C5C", "#0C2C56", "#FFFFFF"],
  TEX: ["#C0111F", "#003278", "#FFFFFF"],
  LAA: ["#BA0021", "#003263", "#C4CED4"],
  ATH: ["#00342C", "#FFB400", "#FFFFFF"],
  WSN: ["#AB0003", "#FFFFFF", "#FFFFFF"],
  NYM: ["#F75500", "#002C6E", "#FFFFFF"],
  MIA: ["#019CD6", "#E51829", "#FFFFFF"],
  PHI: ["#C21D39", "#01396F", "#FFFFFF"],
  ATL: ["#D3042B", "#001D3D", "#FFFFFF"],
  CIN: ["#C6011F", "#000000", "#FFFFFF"],
  PIT: ["#F7BF02", "#000000", "#FFFFFF"],
  CHC: ["#123581", "#E62F2E", "#FFFFFF"],
  MIL: ["#18294C", "#FEC430", "#FFFFFF"],
  STL: ["#BE0A15", "#FDDB00", "#FFFFFF"],
  LAD: ["#005A9D", "#FFFFFF", "#FFFFFF"],
  ARI: ["#C71128", "#00C2CE", "#FFFFFF"],
  SDP: ["#2D221C", "#FFC125", "#FFFFFF"],
  SFG: ["#231F20", "#F4793E", "#E8D8C4"],
  COL: ["#503E80", "#BFC2C9", "#FFFFFF"],
  FA: ["#BFBFBF", "#FFFFFF", "#FFFFFF"],
};

export default function StatCard({
  playerDataProp,
  playerInfoProp,
  seasonStatProp,
  isDesktop,
  season,
  level
}: {
  playerDataProp: any;
  playerInfoProp: any;
  seasonStatProp: any;
  isDesktop: boolean;
  season: any;
  level?: any;
}) {
  //console.log("PDP: ", playerDataProp);
  //console.log("SSP", seasonStatProp)
  //console.log(Object.keys(seasonStatProp))
  //console.log(seasonStatProp["wRC+"])

  // AA / High-A are aggregate (non-Statcast). At Single-A, the California /
  // Carolina leagues (aball_src === "agg") are aggregate too -- same as AA/A+,
  // including zone discipline -- while the FSL (aball_src === "fsl") keeps its
  // full Statcast layout.
  const isABallAgg = level === "A" && playerDataProp?.aball_src === "agg";
  // Rookie ball (Complex + DSL) uses the aggregate (non-Statcast) stat layout.
  const isAgg = level === "AA" || level === "A+" || isABallAgg || level === "Rk";

  // Many Rookie-ball (esp. DSL) players have no FanGraphs profile, so
  // playerInfoProp.common is undefined. Fall back to our own CSV data for the
  // team header and the player's position so the aggregate stat tables still
  // render. (The Statcast layout requires FanGraphs data and is never used at
  // aggregate levels anyway.)
  const hasCommon = playerInfoProp?.common !== undefined;
  const teamAbb = hasCommon
    ? playerInfoProp.common.teamInfo?.MLB_AbbName
    : (playerDataProp?.MLB_AbbName ?? playerDataProp?.team);
  const position = hasCommon
    ? playerInfoProp.common.playerInfo?.Position
    : playerDataProp?.Position;
  const teamCol = (i: number) =>
    teamAbb && teamColors[teamAbb] ? teamColors[teamAbb][i] : teamColors["FA"][i];

  return (
    <div>
      {(hasCommon || (isAgg && playerDataProp)) && (
        <div>
          <div
            style={{
              padding: "10px",
              backgroundColor: teamCol(0),
            }}
          >
            {isDesktop &&
              (<Grid
                container
                direction="row"
                justifyContent="center"
                alignItems="center"
              >
                <Typography variant="h5">
                  <span
                    style={{
                      color: teamCol(1),
                      fontWeight: "bold",
                      fontSize: "25pt",
                      textAlign: "center"
                    }}
                  >
                    {season} Season Stats
                  </span>
                </Typography>
              </Grid>)}
            {!isDesktop &&
              (<Grid
                container
                direction="row"
                justifyContent="center"
                alignItems="center"
              ><Typography variant="h5" style={{ fontSize: "25pt" }}>
                  <div
                    style={{
                      color: teamCol(1),
                      fontWeight: "bold",
                    }}
                  >
                    {season} Season Stats
                  </div>
                </Typography></Grid>)}
          </div>
          <div style={{ margin: "10px" }}>
            {(position !== "P" && isAgg) && (
              // AA hitters: no Statcast (EV/LA/Barrels/xStats) and no
              // BA/OBP/SLG/hits component counts. Show available aggregate +
              // plate-discipline stats only.
              <div>
                <Typography variant="h5" style={{ fontSize: "20pt", marginLeft: "10px" }}>
                  Standard
                </Typography>
                <TableContainer component={Paper} >
                  <Table size="small" padding="none" sx={{ marginLeft: "10px", maxWidth: "95%", marginTop: "5px", marginBottom: "15px" }}>
                    <TableHead>
                      <TableRow>
                        <TableCell >AB</TableCell>
                        <TableCell >PA</TableCell>
                        <TableCell >HR</TableCell>
                        <TableCell >BB%</TableCell>
                        <TableCell >K%</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow key={"standard"}>
                        <TableCell >{playerDataProp.ab}</TableCell>
                        <TableCell >{playerDataProp.pa}</TableCell>
                        <TableCell >{playerDataProp.hr}</TableCell>
                        <TableCell >{Number(playerDataProp.bbrate).toFixed(1)}</TableCell>
                        <TableCell >{Number(playerDataProp.krate).toFixed(1)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography variant="h5" style={{ fontSize: "20pt", marginLeft: "10px", marginTop: "20px" }}>
                  Advanced
                </Typography>
                <TableContainer component={Paper} >
                  <Table size="small" padding="none" sx={{ width: "95%", marginLeft: "10px", marginTop: "5px", marginBottom: "15px" }}>
                    <TableHead>
                      <TableRow>
                        <TableCell >wOBA</TableCell>
                        <TableCell >wOBACON</TableCell>
                        <TableCell >wRC+</TableCell>
                        <TableCell >ISO</TableCell>
                        <TableCell >BABIP</TableCell>
                        <TableCell >Spd</TableCell>
                        <TableCell >GB%</TableCell>
                        <TableCell >Pull Air%</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow key={"advanced"}>
                        <TableCell >{Number(playerDataProp.woba).toFixed(3).toString().replace(/^0/, "")}</TableCell>
                        <TableCell >{playerDataProp.wobacon != null ? Number(playerDataProp.wobacon).toFixed(3).toString().replace(/^0/, "") : "-"}</TableCell>
                        <TableCell >{Number(playerDataProp.wrcplus).toFixed(0)}</TableCell>
                        <TableCell >{Number(playerDataProp.iso).toFixed(3).toString().replace(/^0/, "")}</TableCell>
                        <TableCell >{Number(playerDataProp.babip).toFixed(3).toString().replace(/^0/, "")}</TableCell>
                        <TableCell >{Number(playerDataProp.spd).toFixed(2)}</TableCell>
                        <TableCell >{playerDataProp.gbrate != null ? Number(playerDataProp.gbrate).toFixed(1) : "-"}</TableCell>
                        <TableCell >{playerDataProp.pullair != null ? Number(playerDataProp.pullair).toFixed(1) : "-"}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography variant="h5" style={{ fontSize: "20pt", marginLeft: "10px", marginTop: "20px" }}>
                  Plate Discipline
                </Typography>
                <TableContainer component={Paper} >
                  <Table size="small" padding="none" sx={{ width: "95%", marginLeft: "10px", marginTop: "5px", marginBottom: "15px" }}>
                    <TableHead>
                      <TableRow>
                        <TableCell >Swing%</TableCell>
                        <TableCell >Chase%</TableCell>
                        <TableCell >Whiff%</TableCell>
                        <TableCell >SwStr%</TableCell>
                        <TableCell >Z-Swing%</TableCell>
                        <TableCell >Z-Con%</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow key={"discipline"}>
                        <TableCell >{Number(playerDataProp.swing).toFixed(1)}</TableCell>
                        <TableCell >{Number(playerDataProp.chaserate).toFixed(1)}</TableCell>
                        <TableCell >{Number(playerDataProp.whiffrate).toFixed(1)}</TableCell>
                        <TableCell >{Number(playerDataProp.swstr).toFixed(1)}</TableCell>
                        <TableCell >{Number(playerDataProp.zswing).toFixed(1)}</TableCell>
                        <TableCell >{Number(playerDataProp.zcontact).toFixed(1)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            )}
            {(position === "P" && isAgg) && (
              // AA pitchers: no Statcast. Show available aggregate +
              // plate-discipline stats only.
              <div>
                <Typography variant="h5" style={{ fontSize: "20pt", marginLeft: "10px" }}>
                  Standard
                </Typography>
                <TableContainer component={Paper} >
                  <Table size="small" padding="none" sx={{ marginLeft: "10px", maxWidth: "95%", marginTop: "5px", marginBottom: "15px" }}>
                    <TableHead>
                      <TableRow>
                        <TableCell >IP</TableCell>
                        <TableCell >ERA</TableCell>
                        <TableCell >FIP</TableCell>
                        <TableCell >BB</TableCell>
                        <TableCell >K</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow key={"standard"}>
                        <TableCell >{seasonStatProp?.IP ?? playerDataProp.ip ?? "-"}</TableCell>
                        <TableCell >{seasonStatProp?.ERA != null ? Number(seasonStatProp.ERA).toFixed(2) : (playerDataProp.era != null ? Number(playerDataProp.era).toFixed(2) : "-")}</TableCell>
                        <TableCell >{seasonStatProp?.FIP != null ? Number(seasonStatProp.FIP).toFixed(2) : (playerDataProp.fip != null ? Number(playerDataProp.fip).toFixed(2) : "-")}</TableCell>
                        <TableCell >{playerDataProp.bb}</TableCell>
                        <TableCell >{playerDataProp.k}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography variant="h5" style={{ fontSize: "20pt", marginLeft: "10px", marginTop: "20px" }}>
                  Advanced
                </Typography>
                <TableContainer component={Paper} >
                  <Table size="small" padding="none" sx={{ width: "95%", marginLeft: "10px", marginTop: "5px", marginBottom: "15px" }}>
                    <TableHead>
                      <TableRow>
                        <TableCell >BB%</TableCell>
                        <TableCell >K%</TableCell>
                        <TableCell >BB/K</TableCell>
                        <TableCell >BABIP</TableCell>
                        <TableCell >GB%</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow key={"advanced"}>
                        <TableCell >{Number(playerDataProp.bbrate).toFixed(1)}</TableCell>
                        <TableCell >{Number(playerDataProp.krate).toFixed(1)}</TableCell>
                        <TableCell >{(playerDataProp.bb / playerDataProp.k).toFixed(2)}</TableCell>
                        <TableCell >{Number(playerDataProp.babip).toFixed(3).toString().replace(/^0/, "")}</TableCell>
                        <TableCell >{playerDataProp.gbrate != null ? Number(playerDataProp.gbrate).toFixed(1) : "-"}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography variant="h5" style={{ fontSize: "20pt", marginLeft: "10px", marginTop: "20px" }}>
                  Batting Against
                </Typography>
                <TableContainer component={Paper} >
                  <Table size="small" padding="none" sx={{ width: "95%", marginLeft: "10px", marginTop: "5px", marginBottom: "15px" }}>
                    <TableHead>
                      <TableRow>
                        <TableCell >BA</TableCell>
                        <TableCell >OBP</TableCell>
                        <TableCell >SLG</TableCell>
                        <TableCell >wOBA</TableCell>
                        <TableCell >Hits</TableCell>
                        <TableCell >2B</TableCell>
                        <TableCell >3B</TableCell>
                        <TableCell >HR</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow key={"against"}>
                        <TableCell >{playerDataProp.ba != null ? Number(playerDataProp.ba).toFixed(3).toString().replace(/^0/, "") : "-"}</TableCell>
                        <TableCell >{playerDataProp.obp != null ? Number(playerDataProp.obp).toFixed(3).toString().replace(/^0/, "") : "-"}</TableCell>
                        <TableCell >{playerDataProp.slg != null ? Number(playerDataProp.slg).toFixed(3).toString().replace(/^0/, "") : "-"}</TableCell>
                        <TableCell >{playerDataProp.woba != null ? Number(playerDataProp.woba).toFixed(3).toString().replace(/^0/, "") : "-"}</TableCell>
                        <TableCell >{playerDataProp.hits ?? "-"}</TableCell>
                        <TableCell >{playerDataProp.doubles ?? "-"}</TableCell>
                        <TableCell >{playerDataProp.triples ?? "-"}</TableCell>
                        <TableCell >{playerDataProp.hr ?? "-"}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography variant="h5" style={{ fontSize: "20pt", marginLeft: "10px", marginTop: "20px" }}>
                  Plate Discipline
                </Typography>
                <TableContainer component={Paper} >
                  <Table size="small" padding="none" sx={{ width: "95%", marginLeft: "10px", marginTop: "5px", marginBottom: "15px" }}>
                    <TableHead>
                      <TableRow>
                        <TableCell >Swing%</TableCell>
                        <TableCell >Chase%</TableCell>
                        <TableCell >Whiff%</TableCell>
                        <TableCell >SwStr%</TableCell>
                        <TableCell >Z-Swing%</TableCell>
                        <TableCell >Z-Con%</TableCell>
                        <TableCell >Swings</TableCell>
                        <TableCell >Whiffs</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow key={"discipline"}>
                        <TableCell >{Number(playerDataProp.swing).toFixed(1)}</TableCell>
                        <TableCell >{Number(playerDataProp.chaserate).toFixed(1)}</TableCell>
                        <TableCell >{Number(playerDataProp.whiffrate).toFixed(1)}</TableCell>
                        <TableCell >{Number(playerDataProp.swstr).toFixed(1)}</TableCell>
                        <TableCell >{Number(playerDataProp.zswing).toFixed(1)}</TableCell>
                        <TableCell >{Number(playerDataProp.zcontact).toFixed(1)}</TableCell>
                        <TableCell >{playerDataProp.swings ?? "-"}</TableCell>
                        <TableCell >{playerDataProp.whiffs ?? "-"}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            )}
            {(position !== "P" && !isAgg && playerDataProp) && (

              // AB PA BA H 1B 2B 3B HR R RBI BB K
              // BB% K% BB/K AVG OBP SLG OPS ISO Spd BABIP WOBA
              // EV LA Barrels HH% Chase% Whiff% xBA xOBP xSLG xWOBA

              <div>
                <Typography variant="h5" style={{ fontSize: "20pt", marginLeft: "10px" }}>
                  Standard
                </Typography>
                <TableContainer component={Paper} >
                  <Table size="small" padding="none" sx={{ marginLeft: "10px", maxWidth: "95%", marginTop: "5px", marginBottom: "15px" }}>
                    <TableHead>
                      <TableRow>
                        <TableCell >AB</TableCell>
                        <TableCell >PA</TableCell>
                        <TableCell >BA</TableCell>
                        <TableCell >H</TableCell>
                        <TableCell >1B</TableCell>
                        <TableCell >2B</TableCell>
                        <TableCell >3B</TableCell>
                        <TableCell >HR</TableCell>
                        <TableCell >BB</TableCell>
                        <TableCell >K</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow
                        key={"standard"}
                      >
                        <TableCell >{playerDataProp.ab}</TableCell>
                        <TableCell >{playerDataProp.pa}</TableCell>
                        <TableCell >{(playerDataProp.ba).toFixed(3).toString().replace(/^0/, "")}</TableCell>
                        <TableCell >{playerDataProp.hits}</TableCell>
                        <TableCell >{playerDataProp.singles}</TableCell>
                        <TableCell >{playerDataProp.doubles}</TableCell>
                        <TableCell >{playerDataProp.triples}</TableCell>
                        <TableCell >{playerDataProp.hr}</TableCell>
                        <TableCell >{playerDataProp.bb}</TableCell>
                        <TableCell >{playerDataProp.k}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography variant="h5" style={{ fontSize: "20pt", marginLeft: "10px", marginTop: "20px" }}>
                  Advanced
                </Typography>
                <TableContainer component={Paper} >
                  <Table size="small" padding="none" sx={{ width: "95%", marginLeft: "10px", marginTop: "5px", marginBottom: "15px" }}>
                    <TableHead>
                      <TableRow>
                        <TableCell >BB%</TableCell>
                        <TableCell >K%</TableCell>
                        <TableCell >BB/K</TableCell>
                        <TableCell >BA</TableCell>
                        <TableCell >OBP</TableCell>
                        <TableCell >SLG</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody >
                      <TableRow
                        key={"advanced"}
                      >
                        <TableCell >{playerDataProp.bbrate}</TableCell>
                        <TableCell >{playerDataProp.krate}</TableCell>
                        <TableCell >{(playerDataProp.bb / playerDataProp.k).toFixed(2)}</TableCell>
                        <TableCell >{(playerDataProp.ba).toFixed(3).toString().replace(/^0/, "")}</TableCell>
                        <TableCell >{(playerDataProp.obp).toFixed(3).toString().replace(/^0/, "")}</TableCell>
                        <TableCell >{(playerDataProp.slg).toFixed(3).toString().replace(/^0/, "")}</TableCell>
                      </TableRow>
                    </TableBody>
                    <TableHead>
                      <TableRow>
                        <TableCell >OPS</TableCell>
                        <TableCell >wRC+</TableCell>
                        <TableCell >ISO</TableCell>
                        <TableCell >Spd</TableCell>
                        <TableCell >BABIP</TableCell>
                        <TableCell >wOBA</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow
                        key={"advanced"}
                      >
                        <TableCell >{(playerDataProp.obp + playerDataProp.slg).toString()[0] === '0' ? (playerDataProp.obp + playerDataProp.slg).toFixed(3).toString().replace(/^0/, "") : (playerDataProp.obp + playerDataProp.slg).toFixed(3).toString() }</TableCell>
                        <TableCell >{seasonStatProp?.["wRC+"] != null ? Number(seasonStatProp["wRC+"]).toFixed(0) : (playerDataProp.wrcplus ? Number(playerDataProp.wrcplus).toFixed(0) : "-")}</TableCell>
                        <TableCell >{(playerDataProp.slg - playerDataProp.ba).toFixed(3).toString().replace(/^0/, "")}</TableCell>
                        <TableCell >{(playerDataProp.spd).toFixed(2)}</TableCell>
                        <TableCell >{(playerDataProp.babip).toFixed(3).toString().replace(/^0/, "")}</TableCell>
                        <TableCell >{(playerDataProp.woba).toFixed(3).toString().replace(/^0/, "")}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography variant="h5" style={{ fontSize: "20pt", marginLeft: "10px", marginTop: "20px" }}>
                  Statcast
                </Typography>
                <TableContainer component={Paper} >
                  <Table size="small" padding="none" sx={{ width: "95%", marginLeft: "10px", marginTop: "5px", marginBottom: "15px" }}>
                    <TableHead>
                      <TableRow>
                        <TableCell >EV</TableCell>
                        <TableCell >LA</TableCell>
                        <TableCell >Brls</TableCell>
                        <TableCell >HH%</TableCell>
                        <TableCell >Chase%</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow
                        key={"statcast"}
                      >
                        <TableCell >{playerDataProp.ev}</TableCell>
                        <TableCell >{playerDataProp.langle}</TableCell>
                        <TableCell >{playerDataProp.barrels}</TableCell>
                        <TableCell >{playerDataProp.hhrate}</TableCell>
                        <TableCell >{playerDataProp.chaserate.toFixed(2)}</TableCell>
                      </TableRow>
                    </TableBody>
                    <TableHead>
                      <TableRow>
                        <TableCell >Whiff%</TableCell>
                        <TableCell >xBA</TableCell>
                        <TableCell >xOBP</TableCell>
                        <TableCell >xSLG</TableCell>
                        <TableCell >xwOBA</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow
                        key={"statcast"}
                      >
                        <TableCell >{playerDataProp.whiffrate}</TableCell>
                        <TableCell >{(playerDataProp.xba).toFixed(3).toString().replace(/^0/, "")}</TableCell>
                        <TableCell >{(playerDataProp.xobp).toFixed(3).toString().replace(/^0/, "")}</TableCell>
                        <TableCell >{(playerDataProp.xslg).toFixed(3).toString().replace(/^0/, "")}</TableCell>
                        <TableCell >{(playerDataProp.xwoba).toFixed(3).toString().replace(/^0/, "")}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>

            )}
            {(position === "P" && !isAgg && playerDataProp) && (
              <div>
                <Typography variant="h5" style={{ fontSize: "20pt", marginLeft: "10px" }}>
                  Standard
                </Typography>
                <TableContainer component={Paper} >
                  <Table size="small" padding="none" sx={{ marginLeft: "10px", maxWidth: "95%" }}>
                    <TableHead>
                      <TableRow>
                        <TableCell >IP</TableCell>
                        <TableCell >ERA</TableCell>
                        <TableCell >FIP</TableCell>
                        <TableCell >AB</TableCell>
                        <TableCell >PA</TableCell>
                        <TableCell >BA</TableCell>
                        <TableCell >H</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow
                        key={"standard"}
                      >
                        <TableCell >{seasonStatProp?.["IP"] ?? playerDataProp.ip ?? "-"}</TableCell>
                        <TableCell >{seasonStatProp?.ERA != null ? Number(seasonStatProp.ERA).toFixed(2) : (playerDataProp.era != null ? Number(playerDataProp.era).toFixed(2) : "-")}</TableCell>
                        <TableCell >{seasonStatProp?.FIP != null ? Number(seasonStatProp.FIP).toFixed(2) : (playerDataProp.fip != null ? Number(playerDataProp.fip).toFixed(2) : "-")}</TableCell>
                        <TableCell >{playerDataProp.ab}</TableCell>
                        <TableCell >{playerDataProp.pa}</TableCell>
                        <TableCell >{(playerDataProp.ba).toFixed(3).toString().replace(/^0/, "")}</TableCell>
                        <TableCell >{playerDataProp.hits}</TableCell>
                      </TableRow>
                    </TableBody>
                    <TableHead>
                      <TableRow>
                        <TableCell >1B</TableCell>
                        <TableCell >2B</TableCell>
                        <TableCell >3B</TableCell>
                        <TableCell >HR</TableCell>
                        <TableCell >BB</TableCell>
                        <TableCell >K</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow
                        key={"standard"}
                      >
                        <TableCell >{playerDataProp.singles}</TableCell>
                        <TableCell >{playerDataProp.doubles}</TableCell>
                        <TableCell >{playerDataProp.triples}</TableCell>
                        <TableCell >{playerDataProp.hr}</TableCell>
                        <TableCell >{playerDataProp.bb}</TableCell>
                        <TableCell >{playerDataProp.k}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography variant="h5" style={{ fontSize: "20pt", marginLeft: "10px", marginTop: "20px" }}>
                  Advanced
                </Typography>
                <TableContainer component={Paper} >
                  <Table size="small" padding="none" sx={{ width: "95%", marginLeft: "10px", marginTop: "5px", marginBottom: "15px" }}>
                    <TableHead>
                      <TableRow>
                        <TableCell >BB%</TableCell>
                        <TableCell >K%</TableCell>
                        <TableCell >BB/K</TableCell>
                        <TableCell >BA</TableCell>
                        <TableCell >BABIP</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow
                        key={"advanced"}
                      >
                        <TableCell >{playerDataProp.bbrate}</TableCell>
                        <TableCell >{playerDataProp.krate}</TableCell>
                        <TableCell >{(playerDataProp.bb / playerDataProp.k).toFixed(2)}</TableCell>
                        <TableCell >{(playerDataProp.ba).toFixed(3).toString().replace(/^0/, "")}</TableCell>
                        <TableCell >{(playerDataProp.babip).toFixed(3).toString().replace(/^0/, "")}</TableCell>
                      </TableRow>
                    </TableBody>
                    <TableHead>
                      <TableRow>
                        <TableCell >OBP</TableCell>
                        <TableCell >SLG</TableCell>
                        <TableCell >OPS</TableCell>
                        <TableCell >ISO</TableCell>
                        
                        <TableCell >wOBA</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow
                        key={"advanced"}
                      >
                        <TableCell >{(playerDataProp.obp).toFixed(3).toString().replace(/^0/, "")}</TableCell>
                        <TableCell >{(playerDataProp.slg).toFixed(3).toString().replace(/^0/, "")}</TableCell>
                        <TableCell >{(playerDataProp.obp + playerDataProp.slg).toString()[0] === '0' ? (playerDataProp.obp + playerDataProp.slg).toFixed(3).toString().replace(/^0/, "") : (playerDataProp.obp + playerDataProp.slg).toFixed(3).toString() }</TableCell>
                        <TableCell >{(playerDataProp.slg - playerDataProp.ba).toFixed(3).toString().replace(/^0/, "")}</TableCell>
                        
                        <TableCell >{(playerDataProp.woba).toFixed(3).toString().replace(/^0/, "")}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography variant="h5" style={{ fontSize: "20pt", marginLeft: "10px", marginTop: "20px" }}>
                  Statcast
                </Typography>
                <TableContainer component={Paper} >
                  <Table size="small" padding="none" sx={{ width: "95%", marginLeft: "10px", marginTop: "5px", marginBottom: "15px" }}>
                    <TableHead>
                      <TableRow>
                        <TableCell >EV</TableCell>
                        <TableCell >LA</TableCell>
                        <TableCell >Brls</TableCell>
                        <TableCell >HH%</TableCell>
                        <TableCell >Chase%</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow
                        key={"statcast"}
                      >
                        <TableCell >{playerDataProp.ev}</TableCell>
                        <TableCell >{playerDataProp.langle}</TableCell>
                        <TableCell >{playerDataProp.barrels}</TableCell>
                        <TableCell >{playerDataProp.hhrate}</TableCell>
                        <TableCell >{playerDataProp.chaserate.toFixed(2)}</TableCell>
                      </TableRow>
                    </TableBody>
                    <TableHead>
                      <TableRow>
                        <TableCell >Whiff%</TableCell>
                        <TableCell >xBA</TableCell>
                        <TableCell >xOBP</TableCell>
                        <TableCell >xSLG</TableCell>
                        <TableCell >xwOBA</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow
                        key={"statcast"}
                      >
                        <TableCell >{playerDataProp.whiffrate}</TableCell>
                        <TableCell >{(playerDataProp.xba).toFixed(3).toString().replace(/^0/, "")}</TableCell>
                        <TableCell >{(playerDataProp.xobp).toFixed(3).toString().replace(/^0/, "")}</TableCell>
                        <TableCell >{(playerDataProp.xslg).toFixed(3).toString().replace(/^0/, "")}</TableCell>
                        <TableCell >{(playerDataProp.xwoba).toFixed(3).toString().replace(/^0/, "")}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            )}

            {position !== "P" && <FieldingTable fielding={playerDataProp?.fielding} />}
          </div>
        </div>
      )}
    </div>
  );
}
